import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { clearInterval, setInterval } from 'worker-timers';
import AuthManager from '../../../store/auth-manager';
import AccessDenied from '../../views/access-denied';
import './protected-route.scss';
import { updateRedirectTo } from '../../../app-root/reducers/routing-reducer';
import { updateUserSigningOut } from '../../../app-root/reducers/user-reducer';
import RouteUrls from '../../../constants/route-urls';
import { validateAuthToken } from '../../../app-root/actions/app-actions';
import {
  clearSessionStorage,
  getFromSessionStorage,
  removeFromSessionStorage,
  setInSessionStorage,
} from '../../../utils/session-storage-utils';
import {
  AUTH_TOKEN,
  LAST_PATHNAME,
  ORGANIZATION_ID,
  ORGANIZATION_USER_ID,
} from '../../../constants/session-storage-keys';
import AnalyticsTrackerInstance from '../../../../analytics';
import APIPerformanceTracker from '../../../app-root/observers/api-performance-observer';
import requestMapping from '../../../app-root/request-mapping';

let refreshIntervalId = null;
const REFRESH_INTERVAL = 60000;

const ProtectedRoute = ({ featureKey, children }) => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const { authenticated, userSigningOut } = useSelector(({ user }) => user);
  const lastPathname = getFromSessionStorage(LAST_PATHNAME);

  useEffect(() => {
    const performanceTracker = new APIPerformanceTracker();
    performanceTracker.trackPerformance(requestMapping.validateAuthToken);
  }, []);

  useEffect(() => {
    if (authenticated) {
      if (lastPathname) {
        removeFromSessionStorage(LAST_PATHNAME);
        dispatch(updateRedirectTo(lastPathname));
      }

      // Only create interval if one doesn't already exist
      if (!refreshIntervalId) {
        refreshIntervalId = setInterval(() => {
          if (getFromSessionStorage(AUTH_TOKEN)) {
            dispatch(validateAuthToken());
          } else {
            clearSessionStorage();
          }
        }, REFRESH_INTERVAL);
      }

      // Log Org Id and User Id in Analytics tool
      AnalyticsTrackerInstance.setMetadata(ORGANIZATION_ID, getFromSessionStorage(ORGANIZATION_ID));
      AnalyticsTrackerInstance.setMetadata('customerId', getFromSessionStorage(ORGANIZATION_USER_ID));
    }
    if (!authenticated && (lastPathname !== pathname)) {
      if (userSigningOut) {
        dispatch(updateUserSigningOut(false));
        removeFromSessionStorage(LAST_PATHNAME);
      } else {
        setInSessionStorage(LAST_PATHNAME, `${pathname}${search}`);
      }
    }

    return () => {
      if (!authenticated && refreshIntervalId) {
        clearInterval(refreshIntervalId);
        clearSessionStorage();
        refreshIntervalId = null;
      }
    };
  }, [authenticated, userSigningOut]);

  if (AuthManager.isAuthenticated()) {
    if (!featureKey || AuthManager.isAuthorised(featureKey)) {
      return children;
    }
    return (
      <div className="protected-route-cont">
        <AccessDenied />
        <div className="pr-padder" />
      </div>
    );
  }
  dispatch(updateRedirectTo(RouteUrls.fallbackForUnauthenticated));

  return null;
};

ProtectedRoute.propTypes = {
  featureKey: PropTypes.string,
  children: PropTypes.element,
};

ProtectedRoute.defaultProps = {
  featureKey: '',
  children: null,
};

export {
  ProtectedRoute as default,
};
